import React from 'react'
import { FormControl, FormHelperText, CircularProgress, TextField, makeStyles } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { debounce } from 'lodash'

export type SearchBoxProps = {
  id: string
  label?: string
  placeholder: string
  disabled?: boolean
  isLoading?: boolean
  autoFocus?: boolean
  value: string
  searchValue: string
  options: { text: string; value: string }[]
  error?: boolean
  helperText?: string
  onBlur?: () => void
  onChange(value: string, text: string): void
  onChangeSearch(value: string): void
  onOpen?: () => void
}

export const SearchBox: React.FC<SearchBoxProps> = ({
  id,
  label,
  placeholder,
  disabled,
  isLoading = false,
  autoFocus,
  value,
  searchValue,
  options,
  error,
  helperText,
  onBlur,
  onChange,
  onChangeSearch,
  onOpen,
}) => {
  const [open, setOpen] = React.useState(false)
  const onChangeSearchDebounced = debounce(onChangeSearch, 500)

  const useStyles = makeStyles({
    option: {
      padding: '0',
    },
    popper: {
      maxHeight: '50%',
      wordBreak: 'break-word',
    },
  })

  return (
    <FormControl className="flex flex-col" error={!!error}>
      {label && (
        <label className={`pb-4 text-lg font-semibold ${disabled ? 'text-body-lighter' : ''}`} htmlFor={id}>
          {label}
        </label>
      )}
      <Autocomplete
        classes={{
          option: useStyles().option,
          popper: useStyles().popper,
        }}
        data-testid={`search-box`}
        id={id}
        open={open}
        disabled={disabled}
        loading={isLoading}
        autoHighlight={false}
        forcePopupIcon={false}
        loadingText="Searching..."
        noOptionsText={searchValue.length >= 3 ? 'No Data Found' : 'Type to search...'}
        value={
          value
            ? {
                value: value,
                text: options.find((item) => item.value === value)?.text ?? value,
              }
            : null
        }
        options={options}
        filterOptions={(options) => options}
        getOptionSelected={(option, value) => option.text === value.text}
        getOptionLabel={(option) => option.text}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            autoFocus={autoFocus}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            style={{
              backgroundColor: 'white',
            }}
            error={!!error}
          />
        )}
        renderOption={(option) => (
          <div
            key={option.value}
            className={`py-4 px-3 w-full border-b border-gray-200 cursor-pointer hover:bg-primary-lighter ${
              option.value === value ? 'bg-primary-lighter' : ''
            }`}
          >
            {option.text}
          </div>
        )}
        onOpen={() => {
          onOpen && onOpen()
          setOpen(true)
        }}
        onClose={() => {
          setOpen(false)
        }}
        onBlur={() => {
          onBlur && onBlur()
          if (!value) onChangeSearch('')
        }}
        onChange={(e, value) => {
          onChange(value?.value ?? '', value?.text ?? '')
          if (!value) onChangeSearch('')
        }}
        onInputChange={(e, inputValue, reason) => {
          if (reason === 'input') {
            if (value) {
              onChange('', '')
              onChangeSearch('')
            } else {
              onChangeSearchDebounced(inputValue.length >= 3 ? inputValue : '')
            }
          }
        }}
      />
      <FormHelperText className="absolute -bottom-6" error={!!error}>
        {helperText}
      </FormHelperText>
    </FormControl>
  )
}
